import { useState } from 'react';
import { normalizeTikTokValue, buildLeadMetadata } from '../utils.js';
import { queryKeys } from '../lib/queryKeys';
import { createLead } from '../api/leads.js';

const parseNegotiationInput = (value) => {
  if (value === undefined || value === null) return null;
  const sanitized = String(value).trim();
  if (!sanitized) return null;
  const normalized = Number(sanitized.replace(/\s+/g, '').replace(',', '.'));
  return Number.isFinite(normalized) ? normalized : null;
};

/**
 * Lead editor hook — manages create lead modal state and submission.
 *
 * @param {object} params
 * @param {Array} params.statuses — status list for fallback
 * @param {object|null} params.user — current user (for ownerId default)
 * @param {function} params.openCreatedLead — the current page's own lead opener (navigate-first)
 * @param {function} params.setIsDetailsOpen
 * @param {function} params.setBusyLeadId
 * @param {import('@tanstack/react-query').QueryClient} params.queryClient
 */
export function useLeadEditor ({
  statuses, user,
  openCreatedLead, setIsDetailsOpen, setBusyLeadId,
  queryClient
}) {
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editorSaving, setEditorSaving] = useState(false);

  const [editorError, setEditorError] = useState(null);

  const openCreateLeadModal = () => {
    if (statuses.length === 0) return;
    setEditorError(null);
    setIsEditorOpen(true);
    setIsDetailsOpen(false);
  };

  const closeEditor = () => {
    setIsEditorOpen(false);
    setEditorError(null);
  };

  const handleEditorSubmit = async (payload, { openCreated = true } = {}) => {
    setEditorSaving(true);
    setEditorError(null);
    let saved = false;
    let created = null;
    let failure = null;
    try {
      const minValue = parseNegotiationInput(payload.negotiationMin);
      const maxValue = parseNegotiationInput(payload.negotiationMax);
      const metadata = buildLeadMetadata(null, {
        negotiationMin: minValue,
        negotiationMax: maxValue,
        payment: payload.payment || null,
        application: payload.application || null,
      });
      if (payload.foundSource) metadata.foundSource = payload.foundSource;
      const payloadBody = {
        ...payload,
        metadata
      };
      delete payloadBody.negotiationMin;
      delete payloadBody.negotiationMax;
      delete payloadBody.payment;
      delete payloadBody.application;
      delete payloadBody.foundSource;

      if (payloadBody.priceMonthly !== undefined) {
        payloadBody.priceMonthly = payloadBody.priceMonthly ? parseFloat(payloadBody.priceMonthly) : null;
      }

      const requestBody = {
        ...payloadBody,
        statusId: payload.statusId || payload.status || statuses.find(s => s.parentId !== null)?.id || null,
        ownerId: payload.ownerId || user?.id || undefined
      };
      delete requestBody.status;
      const lead = await createLead(requestBody);
      queryClient.invalidateQueries({ queryKey: ['leads'] });

      queryClient.invalidateQueries({ queryKey: queryKeys.tags.all });

      if (lead?.id) {
        queryClient.invalidateQueries({ queryKey: queryKeys.accounts.detail(lead.id) });
      }

      if (openCreated) openCreatedLead(lead.id);
      saved = true;
      created = lead;
    } catch (err) {
      console.error('Failed to create lead', err);

      setEditorError({
        reason: err?.data?.reason || err?.message || null,
        duplicate: err?.data?.duplicates?.[0] || null,
      });

      failure = {
        reason: err?.data?.reason || err?.message || null,
        duplicate: err?.data?.duplicates?.[0] || null,
        code: err?.data?.error ?? null,
        status: err?.status ?? null,
      };
    } finally {
      setEditorSaving(false);
      setBusyLeadId(null);
    }

    if (saved) {
      setIsEditorOpen(false);
    }

    return saved ? { ok: true, lead: created } : { ok: false, ...failure };
  };

  return {
    isEditorOpen, editorSaving, editorError,
    openCreateLeadModal,
    handleEditorSubmit, closeEditor
  };
}
